import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Calendar } from '@/components/ui/calendar'
import {
  registerNestedOverlayClose,
  registerNestedOverlayOpen,
} from '@/lib/dialogDismissGuard'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import AsyncSelect from 'react-select/async'
import { Formik, ErrorMessage, FormikHelpers, FieldArray } from 'formik'
import * as Yup from 'yup'
import {
  useGetEmployeeListQuery,
  useUpdateProjectMetaMutation,
  // useUpdateProjectDetailsMutation,
  // useCreateProjectDetailsMutation,
  useUpdateProjectScopeMutation,
  useDeleteProjectMilestoneMutation,
  useDeleteProjectTeamHoursMutation,
  type Project,
  type ProjectDetails,
  type ProjectLink,
  type ProjectScopeData,
  useGetDetailsQuery,
} from '@/redux/service/project'
import {
  useLazyGetClientsQuery,
  // useCreateClientMutation,
  useUpdateClientMutation,
} from '@/redux/service/client'
import {
  useLazyGetVendorsQuery,
  useGetVendorOptionsQuery,
} from '@/redux/service/vendor'
import {
  Users,
  Calendar as CalendarIcon,
  CalendarDays,
  Briefcase,
  Plus,
  Loader2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  ExternalLink,
  FileSignature,
  ListTree,
  FileQuestion,
  Building2,
  AlertCircle,
} from 'lucide-react'
import { ClickUpIcon } from '@/components/icons/ClickUpIcon'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { toast } from 'react-hot-toast'
import ProjectView from './ProjectView'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import CreateUpdateProject from './CreateUpdateProject'
import ProjectDetailsList from './ProjectDetailsList'
import VendorHoursList from './VendorHoursList'
import VendorPaymentSchedule from './VendorPaymentSchedule'
import { useCheckPermission } from '@/hooks/useCheckPermission'

interface ProjectModelProps {
  readonly isDialogOpen: string | null
  readonly setIsDialogOpen: () => void
  readonly data?: Project | null
  readonly globalIndex?: number
  readonly totalCount?: number
  readonly hasPrev?: boolean
  readonly hasNext?: boolean
  readonly onPrev?: () => void
  readonly onNext?: () => void
}

const ProjectMetaSchema = Yup.object().shape({
  project_type: Yup.string(),
  assignee_username: Yup.string(),

  archived: Yup.boolean(),
  client: Yup.object()
    .shape({
      value: Yup.mixed().required(),
      label: Yup.string().required(),
    })
    .nullable(),
  has_vendor_work: Yup.boolean(),
  vendor: Yup.object()
    .shape({
      value: Yup.number().required(),
      label: Yup.string().required(),
    })
    .nullable()
    .when('has_vendor_work', {
      is: true,
      then: schema =>
        schema.required('Vendor is required when vendor work is enabled'),
      otherwise: schema => schema.nullable(),
    }),
  vendor_payment_type: Yup.string()
    .nullable()
    .when('has_vendor_work', {
      is: true,
      then: schema => schema.required('Payment type is required'),
      otherwise: schema => schema.nullable(),
    }),
  scope: Yup.object().shape({
    teams: Yup.array().of(
      Yup.object().shape({
        employee_id: Yup.number().required('Employee is required'),
        hours: Yup.number().required('Hours are required'),
      })
    ),
    milestones: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Milestone name is required'),
        is_completed: Yup.boolean(),
        start_date: Yup.string().required('Start date is required'),
        end_date: Yup.string().required('End date is required'),
      })
    ),
  }),
})

interface MetaFormValues {
  project_type: string
  assignee_username: string
  archived: boolean
  client: {
    // ClickUp task id (clickup_people.id) — always a string, never coerce
    // with Number() or it becomes NaN and every client lookup 404s.
    value: string
    label: string
    projectIds?: string[]
  } | null
  has_vendor_work: boolean
  vendor: {
    value: number
    label: string
    rate?: number
  } | null
  vendor_payment_type: 'full' | 'milestone' | null
  scope: ScopeFormValues
}

interface ScopeFormValues {
  teams: { id?: number; employee_id: number | string; hours: number | string }[]
  milestones: {
    id?: number
    name: string
    is_completed: boolean
    start_date: string | null
    end_date: string | null
  }[]
}

// ponytail: Project Scope card is disabled below, not deleted - flip to
// true (or move team management elsewhere) to bring it back.
const SHOW_PROJECT_SCOPE = false

const linkIconFor = (type: string): ReactNode => {
  switch (type) {
    case 'clickup':
      return <ClickUpIcon className='h-[14px] w-[14px]' />
    case 'schedule':
      return <CalendarDays className='h-[14px] w-[14px]' />
    case 'contract':
      return <FileSignature className='h-[14px] w-[14px]' />
    case 'wbs':
      return <ListTree className='h-[14px] w-[14px]' />
    case 'why_doc':
      return <FileQuestion className='h-[14px] w-[14px]' />
    default:
      return <ExternalLink className='h-[14px] w-[14px]' />
  }
}

const buildProjectLinks = (
  project: Project | null | undefined
): ProjectLink[] => {
  if (!project) return []

  const baseLinks = project.links ?? []
  const hasSchedule = baseLinks.some(
    l => l.type === 'schedule' || l.url === project.url
  )
  const links: ProjectLink[] =
    project.url && !hasSchedule
      ? [
          ...baseLinks,
          { type: 'schedule', label: 'Schedule', url: project.url },
        ]
      : [...baseLinks]

  const typesPresent = new Set(links.map(l => l.type))
  const extras: ProjectLink[] = []
  if (project.why_doc && !typesPresent.has('why_doc')) {
    extras.push({ type: 'why_doc', label: 'Why Doc', url: project.why_doc })
  }
  if (project.wbs && !typesPresent.has('wbs')) {
    extras.push({ type: 'wbs', label: 'WBS', url: project.wbs })
  }
  if (project.contract_link && !typesPresent.has('contract')) {
    extras.push({
      type: 'contract',
      label: 'Contract',
      url: project.contract_link,
    })
  }
  if (project.clickup_list_url && !typesPresent.has('clickup')) {
    extras.push({
      type: 'clickup',
      label: 'ClickUp',
      url: project.clickup_list_url,
    })
  }

  return [...links, ...extras]
}

export default function ProjectModel({
  isDialogOpen,
  setIsDialogOpen,
  data,
  globalIndex,
  totalCount,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: ProjectModelProps) {
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false)
  const [editingDetail, setEditingDetail] = useState<ProjectDetails | null>(
    null
  )

  /* Vendor + payment type are saved the moment both are picked (not gated
     behind the big "Save Changes" button below), so the summary panel can
     show real, working data right away instead of only after a full save.
     This override is layered on top of `data` until the parent's project
     list refetches and `data` itself catches up. */
  const [vendorLinkOverride, setVendorLinkOverride] = useState<{
    has_vendor_work: boolean
    vendor_id: number | null
    vendor_name: string | null
    vendor_payment_type: 'full' | 'milestone' | null
  } | null>(null)
  const [isSavingVendorLink, setIsSavingVendorLink] = useState(false)

  useEffect(() => {
    setVendorLinkOverride(null)
  }, [data?.project_id])

  const [updateProjectMeta] = useUpdateProjectMetaMutation()
  const [updateProjectScope] = useUpdateProjectScopeMutation()
  const [deleteProjectMilestone] = useDeleteProjectMilestoneMutation()
  const [deleteProjectTeamHours] = useDeleteProjectTeamHoursMutation()

  const isEditMode = isDialogOpen === '1'
  const closeDialog = () => setIsDialogOpen()
  const { data: employeeList } = useGetEmployeeListQuery(undefined, {
    skip: !data,
  })
  const [searchClients, { isFetching: isSearchingClients }] =
    useLazyGetClientsQuery()
  const [searchVendors, { isFetching: isSearchingVendors }] =
    useLazyGetVendorsQuery()
  /* Cheap, unfiltered list used only to seed the vendor select's default
     (pre-search) options; typed search still goes through /vendors. */
  const { data: vendorOptionsData } = useGetVendorOptionsQuery(undefined, {
    skip: !data,
  })

  type ClientOption = {
    value: string
    label: string
    projectIds: string[]
  }

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => clearTimeout(searchDebounceRef.current)
  }, [])

  const loadClientOptions = (
    inputValue: string,
    callback: (options: ClientOption[]) => void
  ) => {
    clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      searchClients({ search: inputValue, page: 1, limit: 20 })
        .unwrap()
        .then(response =>
          callback(
            response.data.map(client => ({
              value: client.client_id,
              label: client.client_name,
              projectIds: client.projects?.map(p => p.project_id) ?? [],
            }))
          )
        )
        .catch(() => callback([]))
    }, 400)
  }
  // const [createClient] = useCreateClientMutation()
  const [updateClient] = useUpdateClientMutation()
  const { hasPageAccess } = useCheckPermission()
  const projectLinks = buildProjectLinks(data)

  const persistVendorLink = async (next: {
    vendor_id: number
    vendor_name: string
    vendor_payment_type: 'full' | 'milestone'
  }) => {
    if (!data?.project_id) return
    setIsSavingVendorLink(true)
    try {
      await updateProjectMeta({
        id: data.project_id,
        data: {
          has_vendor_work: true,
          vendor_id: next.vendor_id,
          vendor_payment_type: next.vendor_payment_type,
        },
      }).unwrap()
      setVendorLinkOverride({ has_vendor_work: true, ...next })
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } }
      toast.error(err?.data?.message ?? 'Failed to save vendor selection')
    } finally {
      setIsSavingVendorLink(false)
    }
  }

  const effectiveVendorLink = vendorLinkOverride ?? {
    has_vendor_work: !!data?.has_vendor_work,
    vendor_id: data?.vendor_id ?? null,
    vendor_name: data?.vendor_name ?? null,
    vendor_payment_type: data?.vendor_payment_type ?? null,
  }

  const { data: fullDetails, isLoading: isdetailsLoading } = useGetDetailsQuery(
    { project_id: data?.project_id || 0 },
    { skip: !data }
  )

  const vendorDefaultOptions = (vendorOptionsData?.data ?? []).map(v => ({
    value: v.vendor_id,
    label: v.vendor_name,
    ...(v.default_rate !== undefined ? { rate: v.default_rate } : {}),
  }))

  const loadVendorOptions = (
    inputValue: string
  ): Promise<{ value: number; label: string; rate?: number }[]> =>
    searchVendors({
      search: inputValue,
      limit: 20,
      is_active: true,
      setup_completed: true,
    })
      .unwrap()
      .then(res =>
        res.data.map(v => ({
          value: v.vendor_id,
          label: v.vendor_name,
          ...(v.default_rate !== undefined ? { rate: v.default_rate } : {}),
        }))
      )
      .catch((): { value: number; label: string; rate?: number }[] => [])

  const metaInitialValues: MetaFormValues = {
    project_type: data?.project_type ?? '',
    assignee_username: data?.assignee_username ?? '',
    archived: !!data?.setup_completed,
    client: data?.client_id
      ? {
          value: String(data.client_id),
          label: data.client_name || 'Unknown Client',
        }
      : null,
    has_vendor_work: !!data?.has_vendor_work,
    vendor: data?.vendor_id
      ? { value: data.vendor_id, label: data.vendor_name || 'Unknown Vendor' }
      : null,
    vendor_payment_type: data?.vendor_payment_type ?? null,
    scope: {
      teams: (fullDetails?.data.teams ?? []).map(t => ({
        ...(t.id !== undefined ? { id: t.id } : {}),
        employee_id: t.employee_id,
        hours: t.hours,
      })),
      milestones: (fullDetails?.data.milestones ?? []).map(m => ({
        ...(m.id !== undefined ? { id: m.id } : {}),
        name: m.milestone_name ?? m.name ?? '',
        is_completed: !!m.is_completed,
        start_date: m.start_date ?? '',
        end_date: m.end_date ?? '',
      })),
    },
  }

  const handleMetaSubmit = async (
    values: MetaFormValues,
    { setSubmitting, resetForm }: FormikHelpers<MetaFormValues>
  ) => {
    if (!data?.project_id) return

    setSubmitting(true)

    const initialClientId = metaInitialValues.client
      ? metaInitialValues.client.value
      : null
    const currentClientId = values.client ? values.client.value : null
    const clientChanged = initialClientId !== currentClientId

    const otherMetaChanged =
      values.project_type !== metaInitialValues.project_type ||
      values.assignee_username !== metaInitialValues.assignee_username ||
      values.archived !== metaInitialValues.archived ||
      values.has_vendor_work !== metaInitialValues.has_vendor_work ||
      (values.vendor?.value ?? null) !==
        (metaInitialValues.vendor?.value ?? null) ||
      values.vendor_payment_type !== metaInitialValues.vendor_payment_type

    const scopeChanged =
      JSON.stringify(values.scope) !== JSON.stringify(metaInitialValues.scope)

    if (!clientChanged && !otherMetaChanged && !scopeChanged) {
      toast('No changes to save')
      setSubmitting(false)
      return
    }

    let resolvedClientId: string | null | undefined = undefined

    if (clientChanged) {
      if (values.client) {
        if (!data.project_task_id) {
          toast.error(
            `"${data.list_name}" isn't linked to a ClickUp Projects Schedule task yet, so it can't be assigned to a client. In ClickUp, add (or fill in the "🔗 ClickUp List" field on) its Projects Schedule task, then try again after the next sync.`,
            { duration: 8000 }
          )
          setSubmitting(false)
          return
        }

        resolvedClientId = values.client.value
        const existingProjectIds = values.client.projectIds ?? []
        const updatedProjectIds = [
          ...new Set([...existingProjectIds, data.project_task_id]),
        ]
        try {
          await updateClient({
            id: resolvedClientId,
            client_name: values.client.label,
            project_ids: updatedProjectIds,
          }).unwrap()
        } catch (error) {
          const err = error as { data?: { message?: string } }
          toast.error(err?.data?.message ?? 'Failed to assign client')
          setSubmitting(false)
          return
        }
      } else {
        resolvedClientId = null
      }
    }

    const metaPayload: {
      project_type: string
      assignee_username: string
      setup_completed: boolean
      archived: boolean
      has_vendor_work: boolean
      vendor_id: number | null
      vendor_payment_type: 'full' | 'milestone' | null
      client_id?: string | null
    } = {
      project_type: values.project_type,
      assignee_username: values.assignee_username,
      setup_completed: values.archived,
      archived: data?.main_archived ?? false,
      has_vendor_work: values.has_vendor_work,
      vendor_id: values.has_vendor_work
        ? values.vendor
          ? Number(values.vendor.value)
          : null
        : null,
      vendor_payment_type: values.has_vendor_work
        ? values.vendor_payment_type
        : null,
    }

    try {
      // Meta is saved first (and awaited) so vendor_payment_type lands
      // before the scope upsert runs - a new milestone saved in the same
      // click auto-pairs a vendor payment schedule row server-side based
      // on the project's *current* payment type.
      if (clientChanged || otherMetaChanged) {
        await updateProjectMeta({
          id: data.project_id,
          data: metaPayload,
        }).unwrap()
      }

      if (scopeChanged) {
        const scopePayload: ProjectScopeData = {
          teams: values.scope.teams
            .filter(t => t.employee_id !== '' && t.employee_id != null)
            .map(t => ({
              ...(t.id !== undefined ? { id: t.id } : {}),
              employee_id: Number(t.employee_id),
              hours: Number(t.hours),
            })),
          milestones: values.scope.milestones
            .filter(m => m.name.trim() !== '')
            .map(m => ({
              ...(m.id !== undefined ? { id: m.id } : {}),
              name: m.name,
              is_completed: m.is_completed,
              ...(m.start_date ? { start_date: m.start_date } : {}),
              ...(m.end_date ? { end_date: m.end_date } : {}),
            })),
        }
        await updateProjectScope({
          project_id: data.project_id,
          data: scopePayload,
        }).unwrap()
      }

      resetForm({ values })
      toast.success('Changes saved successfully')
      closeDialog()
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } }
      toast.error(err?.data?.message ?? 'Failed to save changes')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={!!isDialogOpen}
      onOpenChange={open => {
        if (!open) closeDialog()
      }}
    >
      <DialogContent className='max-w-[95vw] max-h-[95vh] p-0 gap-0 overflow-auto bg-background/95 backdrop-blur-sm border-border/60 shadow-2xl rounded-2xl'>
        {/* Header Strip */}
        <div className='px-6 py-3.5 border-b border-border bg-muted/40 flex items-center justify-between gap-4'>
          <div className='flex items-center gap-2.5 min-w-0 flex-1'>
            <div className='flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 shrink-0'>
              <Briefcase className='w-3.5 h-3.5 text-primary' />
            </div>
            <span className='text-sm font-semibold text-foreground truncate'>
              {data?.list_name ?? 'Untitled Project'}
            </span>
            {isEditMode && (
              <span className='px-2 py-0.5 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shrink-0'>
                Editing
              </span>
            )}
            {data?.status && (
              <span className='px-2 py-0.5 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shrink-0 capitalize'>
                {data.status}
              </span>
            )}
          </div>
          <div className='flex items-center gap-4 shrink-0'>
            {projectLinks.length > 0 && (
              <div className='flex items-center flex-nowrap gap-x-3 text-[11px] whitespace-nowrap'>
                {projectLinks.map(link => (
                  <a
                    key={link.type}
                    href={link.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1 text-muted-foreground hover:text-primary hover:underline transition-colors'
                    title={`Open ${link.label} in new tab`}
                  >
                    {linkIconFor(link.type)}
                    <span>{link.label}</span>
                    <ExternalLink className='h-2.5 w-2.5 opacity-60' />
                  </a>
                ))}
              </div>
            )}
            {typeof totalCount === 'number' &&
              totalCount > 0 &&
              typeof globalIndex === 'number' &&
              globalIndex > 0 && (
                <div className='flex items-center gap-1 mr-8'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    disabled={!hasPrev}
                    onClick={onPrev}
                    className='h-8 w-8'
                    aria-label='Previous project'
                  >
                    <ChevronLeft className='w-4 h-4' />
                  </Button>
                  <span className='text-xs font-medium text-muted-foreground tabular-nums min-w-[70px] text-center'>
                    {globalIndex} of {totalCount}
                  </span>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    disabled={!hasNext}
                    onClick={onNext}
                    className='h-8 w-8'
                    aria-label='Next project'
                  >
                    <ChevronRight className='w-4 h-4' />
                  </Button>
                </div>
              )}
          </div>
        </div>

        <div className='flex-1 overflow-y-auto p-6 md:p-8'>
          {(() => {
            const isLoadingDetails = isdetailsLoading && isEditMode
            if (isLoadingDetails) {
              return (
                <div className='flex flex-col items-center justify-center py-24 gap-3'>
                  <div className='w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin' />
                  <p className='text-sm text-muted-foreground font-medium'>
                    Loading project details…
                  </p>
                </div>
              )
            }
            if (isEditMode) {
              return (
                <div className='space-y-8'>
                  {/* Project Details — manages its own state via drawer */}
                  <Card className='border-border shadow-sm bg-card'>
                    <CardContent className='pt-6'>
                      <ProjectDetailsList
                        projectId={data?.project_id || 0}
                        showActions={true}
                        onEdit={d => {
                          setEditingDetail(d)
                          setDetailsDrawerOpen(true)
                        }}
                        headerActions={
                          (hasPageAccess('finance') ||
                            hasPageAccess('mini_finance')) && (
                            <Button
                              size='sm'
                              onClick={() => {
                                setEditingDetail(null)
                                setDetailsDrawerOpen(true)
                              }}
                            >
                              <Plus className='w-4 h-4 mr-1' /> Add New
                            </Button>
                          )
                        }
                      />
                    </CardContent>
                  </Card>

                  {/* Project Metadata — own Formik so details refetch never resets these values */}
                  <Formik<MetaFormValues>
                    enableReinitialize
                    initialValues={metaInitialValues}
                    validationSchema={ProjectMetaSchema}
                    onSubmit={handleMetaSubmit}
                  >
                    {({
                      handleSubmit: submitMeta,
                      handleChange,
                      setFieldValue,
                      values,
                      isSubmitting,
                    }) => (
                      <>
                        <Card className='border-border shadow-sm bg-card'>
                          <CardHeader className='pb-4'>
                            <div className='flex items-center gap-2.5'>
                              <div className='flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 shrink-0'>
                                <Briefcase className='w-3.5 h-3.5 text-primary' />
                              </div>
                              <div>
                                <h3 className='text-sm font-semibold leading-tight'>
                                  Project Metadata
                                </h3>
                                <p className='text-xs text-muted-foreground mt-0.5'>
                                  Update client and project status
                                </p>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className='space-y-6'>
                              <div className='space-y-2'>
                                <Label htmlFor='client'>Client</Label>
                                <AsyncSelect
                                  cacheOptions
                                  defaultOptions
                                  loadOptions={loadClientOptions}
                                  isClearable
                                  isLoading={isSearchingClients}
                                  value={values.client}
                                  onChange={newValue =>
                                    setFieldValue('client', newValue)
                                  }
                                  onMenuOpen={registerNestedOverlayOpen}
                                  onMenuClose={registerNestedOverlayClose}
                                  className='text-sm'
                                  classNames={{
                                    control: () =>
                                      '!bg-background !border-input !min-h-[36px] !rounded-md !shadow-none hover:!border-primary/50 hover:!bg-accent/50 !transition-all',
                                    menu: () =>
                                      '!bg-popover !border !border-border !rounded-lg !shadow-lg !z-50 !mt-2 !overflow-hidden',
                                    option: state =>
                                      cn(
                                        '!text-sm !px-3 !py-2 !transition-colors !cursor-pointer',
                                        state.isSelected
                                          ? '!bg-accent/50 !text-primary !font-medium'
                                          : state.isFocused
                                            ? '!bg-accent !text-accent-foreground'
                                            : '!text-popover-foreground'
                                      ),
                                    singleValue: () => '!text-foreground',
                                    input: () => '!text-foreground',
                                  }}
                                  placeholder='Search client...'
                                />
                                <ErrorMessage
                                  name='client'
                                  component='p'
                                  className='text-xs text-destructive font-medium'
                                />
                              </div>

                              {/* Vendor Work Section */}
                              <div className='space-y-3'>
                                <div className='flex items-center justify-between py-3 border-y border-border'>
                                  <div className='flex items-center gap-3'>
                                    <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 shrink-0'>
                                      <Building2 className='w-4 h-4 text-orange-500' />
                                    </div>
                                    <div>
                                      <p className='text-sm font-medium leading-tight'>
                                        Has Vendor Work?
                                      </p>
                                      <p className='text-xs text-muted-foreground mt-0.5'>
                                        Project includes externally outsourced
                                        work
                                      </p>
                                    </div>
                                  </div>
                                  <Switch
                                    checked={values.has_vendor_work}
                                    onCheckedChange={checked => {
                                      setFieldValue('has_vendor_work', checked)
                                      if (!checked) {
                                        setFieldValue('vendor', null)
                                        setFieldValue(
                                          'vendor_payment_type',
                                          null
                                        )
                                        setVendorLinkOverride({
                                          has_vendor_work: false,
                                          vendor_id: null,
                                          vendor_name: null,
                                          vendor_payment_type: null,
                                        })
                                      }
                                    }}
                                    className='data-[state=checked]:bg-orange-500'
                                  />
                                </div>

                                {values.has_vendor_work && (
                                  <div className='space-y-4 p-4 rounded-xl bg-orange-500/5 border border-orange-500/20'>
                                    <div className='space-y-2'>
                                      <div className='flex items-center justify-between'>
                                        <Label htmlFor='vendor'>
                                          Vendor{' '}
                                          <span className='text-destructive'>
                                            *
                                          </span>
                                        </Label>
                                      </div>
                                      <AsyncSelect
                                        key={`vs-${vendorOptionsData?.data.length ?? 0}`}
                                        cacheOptions
                                        defaultOptions={vendorDefaultOptions}
                                        loadOptions={loadVendorOptions}
                                        isClearable
                                        isDisabled={isSavingVendorLink}
                                        isLoading={isSearchingVendors}
                                        value={values.vendor}
                                        onChange={newValue => {
                                          setFieldValue('vendor', newValue)
                                          if (
                                            newValue &&
                                            values.vendor_payment_type
                                          ) {
                                            persistVendorLink({
                                              vendor_id: Number(newValue.value),
                                              vendor_name: newValue.label,
                                              vendor_payment_type:
                                                values.vendor_payment_type,
                                            })
                                          }
                                        }}
                                        className='text-sm'
                                        classNames={{
                                          control: () =>
                                            '!bg-background !border-input !min-h-[36px] !rounded-md !shadow-none hover:!border-orange-500/50 hover:!bg-accent/50 !transition-all',
                                          menu: () =>
                                            '!bg-popover !border !border-border !rounded-lg !shadow-lg !z-50 !mt-2 !overflow-hidden',
                                          option: state =>
                                            cn(
                                              '!text-sm !px-3 !py-2 !transition-colors !cursor-pointer',
                                              state.isSelected
                                                ? '!bg-orange-500/10 !text-orange-600 !font-medium'
                                                : state.isFocused
                                                  ? '!bg-accent !text-accent-foreground'
                                                  : '!text-popover-foreground'
                                            ),
                                          singleValue: () => '!text-foreground',
                                          input: () => '!text-foreground',
                                        }}
                                        placeholder='Search vendor...'
                                        noOptionsMessage={() =>
                                          'No vendors found. Vendors sync from ClickUp.'
                                        }
                                      />
                                      <ErrorMessage
                                        name='vendor'
                                        component='p'
                                        className='text-xs text-destructive font-medium'
                                      />
                                    </div>

                                    <div className='space-y-2'>
                                      <Label>
                                        Payment Type{' '}
                                        <span className='text-destructive'>
                                          *
                                        </span>
                                      </Label>
                                      <div className='flex gap-2'>
                                        <button
                                          type='button'
                                          disabled={isSavingVendorLink}
                                          onClick={() => {
                                            setFieldValue(
                                              'vendor_payment_type',
                                              'full'
                                            )
                                            if (values.vendor) {
                                              persistVendorLink({
                                                vendor_id: Number(
                                                  values.vendor.value
                                                ),
                                                vendor_name:
                                                  values.vendor.label,
                                                vendor_payment_type: 'full',
                                              })
                                            }
                                          }}
                                          className={cn(
                                            'flex-1 py-2 px-3 rounded-lg text-xs font-medium border transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                                            values.vendor_payment_type ===
                                              'full'
                                              ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                                              : 'bg-card border-border text-muted-foreground hover:border-orange-500/50 hover:text-foreground'
                                          )}
                                        >
                                          Full Payment
                                        </button>
                                        <button
                                          type='button'
                                          disabled={isSavingVendorLink}
                                          onClick={() => {
                                            setFieldValue(
                                              'vendor_payment_type',
                                              'milestone'
                                            )
                                            if (values.vendor) {
                                              persistVendorLink({
                                                vendor_id: Number(
                                                  values.vendor.value
                                                ),
                                                vendor_name:
                                                  values.vendor.label,
                                                vendor_payment_type:
                                                  'milestone',
                                              })
                                            }
                                          }}
                                          className={cn(
                                            'flex-1 py-2 px-3 rounded-lg text-xs font-medium border transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                                            values.vendor_payment_type ===
                                              'milestone'
                                              ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                                              : 'bg-card border-border text-muted-foreground hover:border-orange-500/50 hover:text-foreground'
                                          )}
                                        >
                                          Milestone-based
                                        </button>
                                      </div>
                                      <ErrorMessage
                                        name='vendor_payment_type'
                                        component='p'
                                        className='text-xs text-destructive font-medium'
                                      />
                                      {isSavingVendorLink && (
                                        <p className='text-xs text-muted-foreground flex items-center gap-1.5'>
                                          <Loader2 className='w-3 h-3 animate-spin' />
                                          Saving vendor selection…
                                        </p>
                                      )}
                                    </div>

                                    {/* Vendor Hours / Payment Schedule — mutually exclusive:
                                        hourly billing (vendor_hours) when the vendor has no
                                        payment type set, or the fixed/milestone schedule
                                        otherwise. A project's vendor cost only ever comes
                                        from one of the two. */}
                                    {data?.project_id &&
                                      effectiveVendorLink.has_vendor_work &&
                                      typeof effectiveVendorLink.vendor_id ===
                                        'number' &&
                                      (effectiveVendorLink.vendor_payment_type ? (
                                        <div className='pt-4 border-t border-orange-500/20'>
                                          <VendorPaymentSchedule
                                            projectId={data.project_id}
                                            vendorId={
                                              effectiveVendorLink.vendor_id
                                            }
                                            vendorName={
                                              effectiveVendorLink.vendor_name ??
                                              'Unknown Vendor'
                                            }
                                            paymentType={
                                              effectiveVendorLink.vendor_payment_type
                                            }
                                          />
                                        </div>
                                      ) : (
                                        <div className='pt-4 border-t border-orange-500/20'>
                                          <VendorHoursList
                                            projectId={data.project_id}
                                            vendorId={
                                              effectiveVendorLink.vendor_id
                                            }
                                            vendorName={
                                              effectiveVendorLink.vendor_name ??
                                              'Unknown Vendor'
                                            }
                                          />
                                        </div>
                                      ))}
                                  </div>
                                )}

                                {values.has_vendor_work && !values.vendor && (
                                  <div className='flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400'>
                                    <AlertCircle className='w-3.5 h-3.5 mt-0.5 shrink-0' />
                                    <p className='text-xs font-medium'>
                                      A vendor must be selected before this
                                      project can be marked as setup complete.
                                    </p>
                                  </div>
                                )}
                              </div>

                              <div className='pt-2 inline-block'>
                                <div
                                  onClick={() => {
                                    if (
                                      !values.archived &&
                                      values.has_vendor_work &&
                                      !values.vendor
                                    ) {
                                      toast.error(
                                        'Select a vendor before marking setup as complete'
                                      )
                                      return
                                    }
                                    setFieldValue('archived', !values.archived)
                                  }}
                                  className={cn(
                                    'group relative flex items-center gap-5 justify-between p-4 rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden',
                                    values.archived
                                      ? 'bg-primary/5 border-primary/30 shadow-sm'
                                      : 'bg-muted/20 border-border hover:border-primary/20 hover:bg-muted/40'
                                  )}
                                >
                                  {values.archived && (
                                    <div className='absolute -right-4 -top-4 w-24 h-24 bg-primary/5 rounded-full blur-2xl animate-pulse' />
                                  )}
                                  <div className='flex items-center gap-4 relative z-10'>
                                    <div
                                      className={cn(
                                        'flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-500',
                                        values.archived
                                          ? 'bg-primary text-white scale-110 shadow-lg shadow-primary/20'
                                          : 'bg-background border border-border text-muted-foreground group-hover:border-primary/30'
                                      )}
                                    >
                                      <CheckCircle2
                                        className={cn(
                                          'w-5 h-5 transition-transform duration-500',
                                          values.archived
                                            ? 'scale-100'
                                            : 'scale-75 opacity-50'
                                        )}
                                      />
                                    </div>
                                    <div className='space-y-0.5'>
                                      <h4
                                        className={cn(
                                          'text-sm font-bold transition-colors',
                                          values.archived
                                            ? 'text-primary'
                                            : 'text-foreground'
                                        )}
                                      >
                                        Project Setup Status
                                      </h4>
                                      <p className='text-xs text-muted-foreground font-medium'>
                                        {values.archived
                                          ? 'Setup verified and completed'
                                          : 'Mark this project as ready for tracking'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className='relative z-10'>
                                    <Switch
                                      id='archived-toggle'
                                      checked={!!values.archived}
                                      onCheckedChange={checked => {
                                        if (
                                          checked &&
                                          values.has_vendor_work &&
                                          !values.vendor
                                        ) {
                                          toast.error(
                                            'Select a vendor before marking setup as complete'
                                          )
                                          return
                                        }
                                        setFieldValue('archived', checked)
                                      }}
                                      className='data-[state=checked]:bg-primary'
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* ponytail: Project Scope card disabled, not deleted -
                            milestones are now added from the Vendor Payment
                            Schedule panel above. Team member management has
                            no replacement yet; flip this back to `true` (or
                            move team management elsewhere) if it's needed. */}
                        {SHOW_PROJECT_SCOPE && (
                          <Card className='border-border shadow-sm bg-card'>
                            <CardHeader className='pb-4'>
                              <div className='flex items-center gap-2.5'>
                                <div className='flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 shrink-0'>
                                  <Users className='w-3.5 h-3.5 text-primary' />
                                </div>
                                <div>
                                  <h3 className='text-sm font-semibold leading-tight'>
                                    Project Scope
                                  </h3>
                                  <p className='text-xs text-muted-foreground mt-0.5'>
                                    Manage team members and milestones
                                  </p>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className='space-y-6'>
                                {/* Milestones Section */}
                                <div className='space-y-4'>
                                  <div className='flex items-center justify-between pb-2 border-b border-border'>
                                    <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                                      Milestones
                                    </span>
                                    <Button
                                      type='button'
                                      variant='outline'
                                      size='sm'
                                      onClick={() =>
                                        setFieldValue('scope.milestones', [
                                          ...values.scope.milestones,
                                          {
                                            name: '',
                                            is_completed: false,
                                            start_date: '',
                                            end_date: '',
                                          },
                                        ])
                                      }
                                      className='h-7 px-2.5 text-xs border-border hover:bg-accent/30'
                                    >
                                      <Plus className='w-3 h-3 mr-1' /> Add
                                      Milestone
                                    </Button>
                                  </div>

                                  <FieldArray name='scope.milestones'>
                                    {() => (
                                      <div className='space-y-3 max-h-[300px] overflow-y-auto pr-2'>
                                        {values.scope.milestones.map(
                                          (_, index) => {
                                            const m =
                                              values.scope.milestones[index]
                                            const startVal = m?.start_date ?? ''
                                            const endVal = m?.end_date ?? ''
                                            return (
                                              <div
                                                key={index}
                                                className='grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 p-3 rounded-lg border border-border bg-muted/30 group items-center'
                                              >
                                                <input
                                                  type='checkbox'
                                                  name={`scope.milestones.${index}.is_completed`}
                                                  checked={
                                                    values.scope.milestones[
                                                      index
                                                    ]?.is_completed ?? false
                                                  }
                                                  onChange={handleChange}
                                                  className='w-4 h-4 accent-primary'
                                                />
                                                <div className='space-y-1 min-w-0'>
                                                  <Input
                                                    name={`scope.milestones.${index}.name`}
                                                    value={
                                                      values.scope.milestones[
                                                        index
                                                      ]?.name ?? ''
                                                    }
                                                    onChange={handleChange}
                                                    placeholder='Milestone name'
                                                    className='h-9 bg-transparent border-none focus-visible:ring-0 px-0 min-w-0'
                                                  />
                                                  <ErrorMessage
                                                    name={`scope.milestones.${index}.name`}
                                                    component='p'
                                                    className='text-xs text-destructive font-medium'
                                                  />
                                                </div>
                                                <div className='space-y-1 min-w-0'>
                                                  <Popover>
                                                    <PopoverTrigger asChild>
                                                      <Button
                                                        type='button'
                                                        variant='outline'
                                                        size='sm'
                                                        className={cn(
                                                          'h-9 w-full justify-start text-left font-normal text-sm bg-transparent border-border/50',
                                                          !startVal &&
                                                            'text-muted-foreground'
                                                        )}
                                                      >
                                                        <CalendarIcon className='mr-1.5 h-3.5 w-3.5 opacity-50 shrink-0' />
                                                        {startVal &&
                                                        !Number.isNaN(
                                                          new Date(
                                                            startVal
                                                          ).getTime()
                                                        ) ? (
                                                          format(
                                                            new Date(startVal),
                                                            'PP'
                                                          )
                                                        ) : (
                                                          <span className='truncate'>
                                                            Start
                                                          </span>
                                                        )}
                                                      </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                      className='w-auto p-0 border border-border shadow-lg rounded-xl'
                                                      align='start'
                                                    >
                                                      <Calendar
                                                        mode='single'
                                                        captionLayout='dropdown'
                                                        numberOfMonths={1}
                                                        fromYear={2018}
                                                        toYear={2035}
                                                        selected={
                                                          startVal
                                                            ? new Date(startVal)
                                                            : undefined
                                                        }
                                                        onSelect={date =>
                                                          setFieldValue(
                                                            `scope.milestones.${index}.start_date`,
                                                            date
                                                              ? format(
                                                                  date,
                                                                  'yyyy-MM-dd'
                                                                )
                                                              : ''
                                                          )
                                                        }
                                                        className='rounded-xl'
                                                      />
                                                    </PopoverContent>
                                                  </Popover>
                                                  <ErrorMessage
                                                    name={`scope.milestones.${index}.start_date`}
                                                    component='p'
                                                    className='text-xs text-destructive font-medium'
                                                  />
                                                </div>

                                                <div className='space-y-1 min-w-0'>
                                                  <Popover>
                                                    <PopoverTrigger asChild>
                                                      <Button
                                                        type='button'
                                                        variant='outline'
                                                        size='sm'
                                                        className={cn(
                                                          'h-9 w-full justify-start text-left font-normal text-sm bg-transparent border-border/50',
                                                          !endVal &&
                                                            'text-muted-foreground'
                                                        )}
                                                      >
                                                        <CalendarIcon className='mr-1.5 h-3.5 w-3.5 opacity-50 shrink-0' />
                                                        {endVal &&
                                                        !Number.isNaN(
                                                          new Date(
                                                            endVal
                                                          ).getTime()
                                                        ) ? (
                                                          format(
                                                            new Date(endVal),
                                                            'PP'
                                                          )
                                                        ) : (
                                                          <span className='truncate'>
                                                            End
                                                          </span>
                                                        )}
                                                      </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                      className='w-auto p-0 border border-border shadow-lg rounded-xl'
                                                      align='start'
                                                    >
                                                      <Calendar
                                                        mode='single'
                                                        captionLayout='dropdown'
                                                        numberOfMonths={1}
                                                        fromYear={2018}
                                                        toYear={2035}
                                                        selected={
                                                          endVal
                                                            ? new Date(endVal)
                                                            : undefined
                                                        }
                                                        onSelect={date =>
                                                          setFieldValue(
                                                            `scope.milestones.${index}.end_date`,
                                                            date
                                                              ? format(
                                                                  date,
                                                                  'yyyy-MM-dd'
                                                                )
                                                              : ''
                                                          )
                                                        }
                                                        className='rounded-xl'
                                                      />
                                                    </PopoverContent>
                                                  </Popover>
                                                  <ErrorMessage
                                                    name={`scope.milestones.${index}.end_date`}
                                                    component='p'
                                                    className='text-xs text-destructive font-medium'
                                                  />
                                                </div>

                                                <Button
                                                  type='button'
                                                  variant='ghost'
                                                  size='sm'
                                                  onClick={async () => {
                                                    const milestone =
                                                      values.scope.milestones[
                                                        index
                                                      ]

                                                    if (
                                                      milestone?.id &&
                                                      data?.project_id
                                                    ) {
                                                      try {
                                                        await deleteProjectMilestone(
                                                          {
                                                            project_id:
                                                              data.project_id,
                                                            milestone_id:
                                                              milestone.id,
                                                          }
                                                        ).unwrap()
                                                        toast.success(
                                                          'Milestone deleted successfully'
                                                        )

                                                        const newMilestones = [
                                                          ...values.scope
                                                            .milestones,
                                                        ]
                                                        newMilestones.splice(
                                                          index,
                                                          1
                                                        )
                                                        setFieldValue(
                                                          'scope.milestones',
                                                          newMilestones
                                                        )
                                                      } catch (error: unknown) {
                                                        const err = error as {
                                                          data?: {
                                                            message?: string
                                                          }
                                                        }
                                                        toast.error(
                                                          err?.data?.message ??
                                                            'Failed to delete milestone'
                                                        )
                                                      }
                                                    } else {
                                                      const newMilestones = [
                                                        ...values.scope
                                                          .milestones,
                                                      ]
                                                      newMilestones.splice(
                                                        index,
                                                        1
                                                      )
                                                      setFieldValue(
                                                        'scope.milestones',
                                                        newMilestones
                                                      )
                                                    }
                                                  }}
                                                  className='h-8 w-8 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/10'
                                                >
                                                  <Trash2 className='w-4 h-4' />
                                                </Button>
                                              </div>
                                            )
                                          }
                                        )}
                                        {values.scope.milestones.length ===
                                          0 && (
                                          <p className='text-xs text-center text-muted-foreground py-6'>
                                            No milestones added yet
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </FieldArray>
                                </div>
                                {/* Teams Section */}
                                <div className='space-y-4 pt-4 border-t border-border'>
                                  <div className='flex items-center justify-between pb-2 border-b border-border'>
                                    <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                                      Project Team
                                    </span>
                                    <Button
                                      type='button'
                                      variant='outline'
                                      size='sm'
                                      onClick={() =>
                                        setFieldValue('scope.teams', [
                                          ...values.scope.teams,
                                          { employee_id: '', hours: 0 },
                                        ])
                                      }
                                      className='h-7 px-2.5 text-xs border-border hover:bg-accent/30'
                                    >
                                      <Plus className='w-3 h-3 mr-1' /> Add
                                      Member
                                    </Button>
                                  </div>

                                  <FieldArray name='scope.teams'>
                                    {() => (
                                      <div className='space-y-3 max-h-[300px] overflow-y-auto pr-2'>
                                        {values.scope.teams.map((_, index) => (
                                          <div
                                            key={index}
                                            className='grid grid-cols-7 gap-2 p-3 rounded-lg border border-border bg-muted/30 group items-center'
                                          >
                                            <div className='col-span-4'>
                                              <Select
                                                value={
                                                  values.scope.teams[
                                                    index
                                                  ]?.employee_id?.toString() ??
                                                  ''
                                                }
                                                onValueChange={value =>
                                                  setFieldValue(
                                                    `scope.teams.${index}.employee_id`,
                                                    value
                                                  )
                                                }
                                              >
                                                <SelectTrigger className='h-9 bg-transparent border-none focus:ring-0'>
                                                  <SelectValue placeholder='Select employee' />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {employeeList?.data?.map(
                                                    (user: {
                                                      user_id: number
                                                      user_name: string
                                                    }) => (
                                                      <SelectItem
                                                        key={user.user_id}
                                                        value={user.user_id.toString()}
                                                      >
                                                        {user.user_name}
                                                      </SelectItem>
                                                    )
                                                  )}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            <div className='col-span-2'>
                                              <Input
                                                type='number'
                                                min={0}
                                                name={`scope.teams.${index}.hours`}
                                                value={
                                                  values.scope.teams[index]
                                                    ?.hours ?? 0
                                                }
                                                onChange={handleChange}
                                                placeholder='Hrs'
                                                className='h-9 bg-transparent border-none focus-visible:ring-0'
                                              />
                                            </div>
                                            <div className='col-span-1 flex justify-end'>
                                              <Button
                                                type='button'
                                                variant='ghost'
                                                size='sm'
                                                onClick={async () => {
                                                  const teamMember =
                                                    values.scope.teams[index]

                                                  if (
                                                    teamMember?.id &&
                                                    data?.project_id
                                                  ) {
                                                    try {
                                                      await deleteProjectTeamHours(
                                                        {
                                                          project_id:
                                                            data.project_id,
                                                          employee_id: Number(
                                                            teamMember.employee_id
                                                          ),
                                                        }
                                                      ).unwrap()
                                                      toast.success(
                                                        'Team member removed successfully'
                                                      )

                                                      const newTeams = [
                                                        ...values.scope.teams,
                                                      ]
                                                      newTeams.splice(index, 1)
                                                      setFieldValue(
                                                        'scope.teams',
                                                        newTeams
                                                      )
                                                    } catch (error: unknown) {
                                                      const err = error as {
                                                        data?: {
                                                          message?: string
                                                        }
                                                      }
                                                      toast.error(
                                                        err?.data?.message ??
                                                          'Failed to remove team member'
                                                      )
                                                    }
                                                  } else {
                                                    const newTeams = [
                                                      ...values.scope.teams,
                                                    ]
                                                    newTeams.splice(index, 1)
                                                    setFieldValue(
                                                      'scope.teams',
                                                      newTeams
                                                    )
                                                  }
                                                }}
                                                className='h-8 w-8 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/10'
                                              >
                                                <Trash2 className='w-4 h-4' />
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                        {values.scope.teams.length === 0 && (
                                          <p className='text-xs text-center text-muted-foreground py-6'>
                                            No team members assigned
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </FieldArray>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Footer Actions */}
                        <div className='flex justify-between items-center pt-4 border-t border-border'>
                          <Button
                            type='button'
                            variant='ghost'
                            onClick={closeDialog}
                            className='h-10 px-6 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all rounded-xl'
                          >
                            Cancel
                          </Button>
                          <Button
                            type='button'
                            onClick={() => submitMeta()}
                            disabled={isSubmitting}
                            className='h-10 px-8 text-sm font-bold min-w-[160px] bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_14px_0_rgba(var(--primary),0.39)] transition-all duration-300 rounded-xl hover:scale-[1.02] active:scale-[0.98]'
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                                Saving Changes…
                              </>
                            ) : (
                              'Save Changes'
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </Formik>

                  <CreateUpdateProject
                    open={detailsDrawerOpen}
                    onClose={() => {
                      setDetailsDrawerOpen(false)
                      setEditingDetail(null)
                    }}
                    project_id={data?.project_id}
                    list_id={data?.list_id}
                    detail={editingDetail}
                    project_type={data?.project_type ?? ''}
                  />
                </div>
              )
            }
            return (
              <>
                {data?.project_id && (
                  <ProjectView
                    project_id={data.project_id}
                    teamName={data?.assignee_username}
                    closeDialog={closeDialog}
                    list_id={data.list_id}
                  />
                )}
              </>
            )
          })()}
        </div>
      </DialogContent>
    </Dialog>
  )
}
